import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { parseCurrency, formatCurrencyInput } from "../../utils/currency";

const ContractModal = ({ isOpen, onClose, onSave, defaultData = null }) => {
  console.log("ContractModal render edildi, isOpen:", isOpen);
  
  // JWT token'dan kullanıcı adını al
  const getUserNameFromToken = () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const payload = token.split('.')[1];
        const decodedPayload = JSON.parse(atob(payload));
        return decodedPayload.data?.username || "Sistem";
      } catch (e) {
        console.error("JWT decode error:", e);
        return "Sistem";
      }
    }
    return "Sistem";
  };
  
  const [formData, setFormData] = useState({
    totalAmount: "",
    monthlyInstallmentAmount: "",
    startDate: "",
    contractMaker: getUserNameFromToken(), // JWT'den kullanıcı adı
  });


  const [isManualMode, setIsManualMode] = useState(false);
  const [manualInstallments, setManualInstallments] = useState([]);
  const [manualInstallmentCount, setManualInstallmentCount] = useState(1);

  useEffect(() => {
    if (isOpen) {
      console.log("Modal açıldı");
      if (defaultData) {
        // Düzenleme modunda mevcut verileri doldur
        setFormData({
          totalAmount: defaultData.totalAmount || "",
          monthlyInstallmentAmount: defaultData.monthlyInstallmentAmount || "",
          startDate: defaultData.startDate || "",
          contractMaker: defaultData.contractMaker || getUserNameFromToken(),
        });
      } else {
        // Yeni sözleşme oluştururken varsayılan başlangıç: bugün
        const today = new Date();
        const defaultStartDate = today.toISOString().split('T')[0];
        setFormData(prev => ({ ...prev, startDate: defaultStartDate, contractMaker: getUserNameFromToken() }));
      }
    }
  }, [isOpen, defaultData]);

  // 50'nin katına yuvarlama fonksiyonu
  const roundToNearest50 = (value) => {
    if (!value || value.trim() === '') return value;
    const numericValue = parseCurrency(value);
    if (isNaN(numericValue) || numericValue === 0) return value;
    return Math.round(numericValue / 50) * 50;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Para alanlarını anında formatlama (yuvarlama yapmadan)
    if (name === "totalAmount" || name === "monthlyInstallmentAmount") {
      const formatted = formatCurrencyInput(value);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Input'tan çıkıldığında 50'nin katına yuvarla
  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "totalAmount" || name === "monthlyInstallmentAmount") {
      if (value && value.trim() !== '') {
        const rounded = roundToNearest50(value);
        setFormData((prev) => ({ ...prev, [name]: formatCurrencyInput(rounded.toString()) }));
      }
    }
  };

  // Taksit sayısını hesaplama fonksiyonu
  const calculateInstallmentCount = () => {
    if (isManualMode) {
      return manualInstallments.length;
    }
    
    const totalAmount = parseCurrency(formData.totalAmount);
    const monthlyInstallmentAmount = parseCurrency(formData.monthlyInstallmentAmount);
    
    if (isNaN(totalAmount) || isNaN(monthlyInstallmentAmount) || monthlyInstallmentAmount <= 0) {
      return 0;
    }
    
    return Math.ceil(totalAmount / monthlyInstallmentAmount);
  };

  // Manuel mod değiştirme
  const handleModeChange = (manual) => {
    setIsManualMode(manual);
      if (manual) {
        // Manuel moda geçerken başlangıç taksitleri oluştur
        const totalAmount = parseCurrency(formData.totalAmount);
        if (totalAmount > 0) {
          const count = Math.max(1, manualInstallmentCount);
          const defaultAmount = Math.floor(totalAmount / count);
          const remainder = totalAmount - (defaultAmount * count);
          
          const installments = [];
          for (let i = 0; i < count; i++) {
            const startDate = new Date(formData.startDate || new Date());
            startDate.setMonth(startDate.getMonth() + i);
            
            installments.push({
              id: i + 1,
              amount: formatCurrencyInput((i === count - 1 ? defaultAmount + remainder : defaultAmount).toString()),
              dueDate: startDate.toISOString().split('T')[0]
            });
          }
          setManualInstallments(installments);
        }
      }
  };

  // Manuel taksit sayısı değiştirme
  const handleInstallmentCountChange = (count) => {
    const newCount = Math.max(1, Math.min(24, count)); // 1-24 arası sınırla
    setManualInstallmentCount(newCount);
    
    if (isManualMode) {
      const totalAmount = parseCurrency(formData.totalAmount);
      if (totalAmount > 0) {
        const defaultAmount = Math.floor(totalAmount / newCount);
        const remainder = totalAmount - (defaultAmount * newCount);
        
        const installments = [];
        for (let i = 0; i < newCount; i++) {
          const startDate = new Date(formData.startDate || new Date());
          startDate.setMonth(startDate.getMonth() + i);
          
          installments.push({
            id: i + 1,
            amount: formatCurrencyInput((i === newCount - 1 ? defaultAmount + remainder : defaultAmount).toString()),
            dueDate: startDate.toISOString().split('T')[0]
          });
        }
        setManualInstallments(installments);
      }
    }
  };

  // Manuel taksit tutarı değiştirme
  const handleManualInstallmentChange = (index, field, value) => {
    const updatedInstallments = [...manualInstallments];
    if (field === 'amount') {
      const formatted = formatCurrencyInput(value);
      updatedInstallments[index].amount = formatted;
    } else {
      updatedInstallments[index][field] = value;
    }
    setManualInstallments(updatedInstallments);
  };

  // Manuel taksit tutarı blur (yuvarlama)
  const handleManualInstallmentBlur = (index, value) => {
    if (value && value.trim() !== '') {
      const rounded = roundToNearest50(value);
      const updatedInstallments = [...manualInstallments];
      updatedInstallments[index].amount = formatCurrencyInput(rounded.toString());
      setManualInstallments(updatedInstallments);
    }
  };

  // Manuel taksitlerin toplam tutarını hesapla
  const getManualTotal = () => {
    return manualInstallments.reduce((total, installment) => {
      return total + parseCurrency(installment.amount);
    }, 0);
  };

  const handleSubmitModal = async (e) => {
    // Form submit'i engelle
    if (e && e.preventDefault) {
      e.preventDefault();
      console.log(">> Modal form submit engellendi");
    }
    
    console.log(">> Modal form submit edildi");
    
    // Temel form verilerini kontrol et
    if (!formData.totalAmount || !formData.startDate || !formData.contractMaker) {
      toast.error("Lütfen toplam tutar, başlangıç tarihi ve sözleşme yapan kişi bilgilerini doldurun!");
      console.log(">> Form alanları eksik");
      return;
    }

    const totalAmount = parseCurrency(formData.totalAmount);

    if (isNaN(totalAmount) || totalAmount <= 0) {
      toast.error("Geçerli bir toplam tutar giriniz!");
      console.log(">> Toplam tutar geçersiz");
      return;
    }

    // Manuel mod kontrolü
    if (isManualMode) {
      if (manualInstallments.length === 0) {
        toast.error("En az bir taksit tanımlanmalı!");
        return;
      }

      // Manuel taksitlerin toplam tutarı kontrol et
      const manualTotal = getManualTotal();
      if (Math.abs(manualTotal - totalAmount) > 0.01) {
        toast.error(`Taksitlerin toplamı (${manualTotal.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺) toplam tutar ile eşleşmiyor!`);
        return;
      }

      // Her taksit için kontrol
      for (let i = 0; i < manualInstallments.length; i++) {
        const installment = manualInstallments[i];
        const amount = parseCurrency(installment.amount);
        
        if (isNaN(amount) || amount <= 0) {
          toast.error(`${i + 1}. taksit için geçerli bir tutar giriniz!`);
          return;
        }

        if (!installment.dueDate) {
          toast.error(`${i + 1}. taksit için vade tarihi seçiniz!`);
          return;
        }
      }

      // Manuel ödeme planı verilerini hazırla
      const paymentPlanData = {
        totalAmount: totalAmount,
        startDate: formData.startDate,
        contractMaker: formData.contractMaker.trim(),
        isManual: true,
        manualInstallments: manualInstallments.map(inst => ({
          id: inst.id,
          amount: parseCurrency(inst.amount),
          dueDate: inst.dueDate
        }))
      };
      
      console.log(">> Manuel ödeme planı verisi kaydediliyor:", paymentPlanData);
      
      try {
        await onSave(paymentPlanData);
        console.log(">> Manuel veri kaydedildi");
        onClose();
      } catch (error) {
        console.error(">> Manuel modal kaydetme hatası:", error);
        toast.error("Sözleşme kaydedilirken bir hata oluştu!");
      }
      
    } else {
      // Otomatik mod kontrolü
      if (!formData.monthlyInstallmentAmount) {
        toast.error("Lütfen aylık taksit tutarını giriniz!");
        return;
      }

      const monthlyInstallmentAmount = parseCurrency(formData.monthlyInstallmentAmount);

      if (isNaN(monthlyInstallmentAmount) || monthlyInstallmentAmount <= 0) {
        toast.error("Geçerli bir aylık taksit tutarı giriniz!");
        console.log(">> Aylık taksit tutarı geçersiz");
        return;
      }

      if (monthlyInstallmentAmount > totalAmount) {
        toast.error("Aylık taksit tutarı toplam tutardan büyük olamaz!");
        console.log(">> Aylık taksit tutarı toplam tutardan büyük");
        return;
      }

      // Otomatik ödeme planı verilerini hazırla
      const paymentPlanData = {
        totalAmount: totalAmount,
        monthlyInstallmentAmount: monthlyInstallmentAmount,
        startDate: formData.startDate,
        contractMaker: formData.contractMaker.trim(),
        isManual: false
      };
      
      console.log(">> Otomatik ödeme planı verisi kaydediliyor:", paymentPlanData);
      
      try {
        await onSave(paymentPlanData);
        console.log(">> Otomatik veri kaydedildi");
        onClose();
      } catch (error) {
        console.error(">> Otomatik modal kaydetme hatası:", error);
        toast.error("Sözleşme kaydedilirken bir hata oluştu!");
      }
    }
  };

  if (!isOpen) return null;

  const installmentCount = calculateInstallmentCount();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Sözleşme Detayları</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmitModal}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sol Kolon - Temel Bilgiler */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Toplam Tutar (₺)</label>
                <input
                  type="text"
                  name="totalAmount"
                  value={formData.totalAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Toplam tutarı girin"
                  inputMode="decimal"
                  required
                />
              </div>

              {/* Taksit Modu Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Taksit Ayarlama Modu</label>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMode"
                      checked={!isManualMode}
                      onChange={() => handleModeChange(false)}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Otomatik</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMode"
                      checked={isManualMode}
                      onChange={() => handleModeChange(true)}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Manuel Ayarla</span>
                  </label>
                </div>
              </div>

              {/* Otomatik Mod Alanları */}
              {!isManualMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aylık Ödeme Tutarı (₺)</label>
                  <input
                    type="text"
                    name="monthlyInstallmentAmount"
                    value={formData.monthlyInstallmentAmount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Aylık ödeyeceği tutarı girin"
                    inputMode="decimal"
                    required={!isManualMode}
                  />
                </div>
              )}

              {/* Manuel Mod Taksit Sayısı */}
              {isManualMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Taksit Sayısı</label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={manualInstallmentCount}
                    onChange={(e) => handleInstallmentCountChange(parseInt(e.target.value) || 1)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Taksit sayısı"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Taksit Başlangıç Tarihi</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  min="1900-01-01"
                  max="2099-12-31"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sözleşmeyi Yapan</label>
                <input
                  type="text"
                  name="contractMaker"
                  value={formData.contractMaker}
                  readOnly
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 cursor-not-allowed"
                  placeholder="Sözleşmeyi yapan kişi"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Bu alan otomatik olarak doldurulmuştur</p>
              </div>

              {/* Otomatik Mod Özet */}
              {!isManualMode && installmentCount > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Tahmini Taksit Sayısı:</strong> {installmentCount} ay
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Son taksit kalan tutarı içerecektir.
                  </p>
                </div>
              )}
            </div>

            {/* Sağ Kolon - Manuel Taksit Listesi */}
            {isManualMode && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Taksit Detayları</h3>
                  {manualInstallments.length > 0 && (
                    <div className="text-sm">
                      <span className="text-gray-600">Toplam: </span>
                      <span className={`font-bold ${Math.abs(getManualTotal() - parseCurrency(formData.totalAmount)) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                        {getManualTotal().toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="max-h-96 overflow-y-auto border rounded-lg">
                  {manualInstallments.map((installment, index) => (
                    <div key={index} className="p-3 border-b last:border-b-0 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">
                          {installment.id}. Taksit
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Tutar (₺)</label>
                          <input
                            type="text"
                            value={installment.amount}
                            onChange={(e) => handleManualInstallmentChange(index, 'amount', e.target.value)}
                            onBlur={(e) => handleManualInstallmentBlur(index, e.target.value)}
                            className="w-full border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500"
                            placeholder="0,00"
                            inputMode="decimal"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Vade Tarihi</label>
                          <input
                            type="date"
                            value={installment.dueDate}
                            onChange={(e) => handleManualInstallmentChange(index, 'dueDate', e.target.value)}
                            className="w-full border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500"
                            min="1900-01-01"
                            max="2099-12-31"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {manualInstallments.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>Toplam tutar ve taksit sayısı girildikten sonra taksit detayları görüntülenecek.</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
            >
              İptal
            </button>
            <button
              type="button"
              onClick={handleSubmitModal}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium shadow-sm"
            >
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractModal;
